import { useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { pointsActivity } from '@/mocks/dashboard';

type ChartMode = 'area' | 'bar';

interface TooltipPayloadItem {
  dataKey: string;
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const earned = payload.find((p) => p.dataKey === 'earned')?.value ?? 0;
  const redeemed = payload.find((p) => p.dataKey === 'redeemed')?.value ?? 0;
  const net = earned - redeemed;

  return (
    <div className="rounded-2xl border border-background-200/90 bg-background-50/95 p-3.5 shadow-2xl backdrop-blur-md min-w-[200px] animate-fade-in">
      <div className="flex items-center justify-between border-b border-background-200/70 pb-2 mb-2.5">
        <span className="text-xs font-bold text-foreground-800 flex items-center gap-1.5">
          <i className="ri-calendar-event-line text-primary-500"></i>
          Day {label}
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground-400">
          Points
        </span>
      </div>

      <div className="space-y-1.5 text-xs">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-foreground-600 font-medium">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-xs shadow-blue-500/40"></span>
            Earned
          </span>
          <span className="font-heading font-bold text-blue-600">
            +{earned.toLocaleString()}k
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-foreground-600 font-medium">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-xs shadow-emerald-500/40"></span>
            Redeemed
          </span>
          <span className="font-heading font-bold text-emerald-600">
            -{redeemed.toLocaleString()}k
          </span>
        </div>

        <div className="border-t border-dashed border-background-200/90 pt-2 mt-2 flex items-center justify-between">
          <span className="text-[11px] font-semibold text-foreground-500">Net Growth</span>
          <span
            className={`font-heading text-xs font-extrabold px-1.5 py-0.5 rounded-md ${net >= 0
                ? 'bg-emerald-500/10 text-emerald-700'
                : 'bg-rose-500/10 text-rose-700'
              }`}
          >
            {net >= 0 ? `+${net.toLocaleString()}k` : `${net.toLocaleString()}k`}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function PointsChart() {
  const [chartMode, setChartMode] = useState<ChartMode>('area');
  const [showEarned, setShowEarned] = useState(true);
  const [showRedeemed, setShowRedeemed] = useState(true);

  // Compute summary totals for header metrics
  const totals = useMemo(() => {
    const totalEarned = pointsActivity.reduce((acc, curr) => acc + curr.earned, 0);
    const totalRedeemed = pointsActivity.reduce((acc, curr) => acc + curr.redeemed, 0);
    const net = totalEarned - totalRedeemed;
    const redemptionRate = totalEarned > 0 ? ((totalRedeemed / totalEarned) * 100).toFixed(1) : '0';
    return { totalEarned, totalRedeemed, net, redemptionRate };
  }, []);

  return (
    <div className="relative overflow-hidden bg-white border border-background-200 rounded-2xl p-5 md:p-6">
      {/* Decorative ambient top glow */}
      <div className="pointer-events-none absolute -left-12 -top-12 h-36 w-36 rounded-full bg-blue-500/5 blur-3xl" />
      <div className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-emerald-500/5 blur-3xl" />

      {/* Header section with Title, Filter, and View Mode Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 relative z-10">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-bold text-lg text-foreground-950">
              Points activity
            </h3>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary-500/10 text-primary-700">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse"></span>
              Live Trend
            </span>
          </div>
        </div>

        {/* Action controls: Chart type toggle & Interactive Series Filters */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Series toggle pills */}
          <div className="flex items-center gap-1.5 p-1 rounded-xl bg-background-100/90 border border-background-200/80 text-xs">
            <button
              type="button"
              onClick={() => setShowEarned((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-semibold transition-all duration-200 cursor-pointer ${showEarned
                  ? 'bg-blue-500 text-white shadow-xs shadow-blue-500/30'
                  : 'text-foreground-400 hover:text-foreground-700 opacity-60'
                }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
              Earned
            </button>
            <button
              type="button"
              onClick={() => setShowRedeemed((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-semibold transition-all duration-200 cursor-pointer ${showRedeemed
                  ? 'bg-emerald-500 text-white shadow-xs shadow-emerald-500/30'
                  : 'text-foreground-400 hover:text-foreground-700 opacity-60'
                }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
              Redeemed
            </button>
          </div>

          {/* Mode switch: Smooth Curve vs Bars */}
          <div className="flex items-center p-1 rounded-xl bg-background-100/90 border border-background-200/80 text-xs">
            <button
              type="button"
              title="Smooth area curve view"
              onClick={() => setChartMode('area')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg font-semibold transition-all duration-200 cursor-pointer ${chartMode === 'area'
                  ? 'bg-background-50 text-foreground-950 shadow-xs border border-background-200/70'
                  : 'text-foreground-500 hover:text-foreground-800'
                }`}
            >
              <i className="ri-line-chart-line text-sm"></i>
              <span className="hidden sm:inline">Curves</span>
            </button>
            <button
              type="button"
              title="Modern bar view"
              onClick={() => setChartMode('bar')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg font-semibold transition-all duration-200 cursor-pointer ${chartMode === 'bar'
                  ? 'bg-background-50 text-foreground-950 shadow-xs border border-background-200/70'
                  : 'text-foreground-500 hover:text-foreground-800'
                }`}
            >
              <i className="ri-bar-chart-fill text-sm"></i>
              <span className="hidden sm:inline">Bars</span>
            </button>
          </div>
        </div>
      </div>

      {/* Summary KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 p-3 rounded-xl bg-background-100/60 border border-background-200/70 text-xs">
        <div>
          <span className="text-foreground-400 font-medium block">Total Earned</span>
          <span className="font-heading font-extrabold text-foreground-950 text-sm md:text-base text-blue-600">
            {totals.totalEarned.toLocaleString()}k
          </span>
        </div>
        <div>
          <span className="text-foreground-400 font-medium block">Total Redeemed</span>
          <span className="font-heading font-extrabold text-foreground-950 text-sm md:text-base text-emerald-600">
            {totals.totalRedeemed.toLocaleString()}k
          </span>
        </div>
        <div>
          <span className="text-foreground-400 font-medium block">Net Points</span>
          <span className="font-heading font-extrabold text-foreground-950 text-sm md:text-base text-indigo-600">
            +{totals.net.toLocaleString()}k
          </span>
        </div>
        <div>
          <span className="text-foreground-400 font-medium block">Burn Ratio</span>
          <span className="font-heading font-extrabold text-foreground-950 text-sm md:text-base text-foreground-800">
            {totals.redemptionRate}%
          </span>
        </div>
      </div>

      {/* Chart visualization area */}
      <div className="h-64 md:h-72 w-full relative z-10">
        <ResponsiveContainer width="100%" height="100%">
          {chartMode === 'area' ? (
            <AreaChart data={pointsActivity} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
              <defs>
                <linearGradient id="earnedAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.45} />
                  <stop offset="70%" stopColor="#3b82f6" stopOpacity={0.08} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.0} />
                </linearGradient>
                <linearGradient id="redeemedAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="70%" stopColor="#10b981" stopOpacity={0.06} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.0} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="4 4"
                vertical={false}
                stroke="rgba(148, 163, 184, 0.25)"
              />

              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: 'rgba(100, 116, 139, 0.8)' }}
                tickFormatter={(val) => `D${val}`}
              />

              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: 'rgba(100, 116, 139, 0.8)' }}
                tickFormatter={(val) => `${val}k`}
              />

              <Tooltip content={<CustomTooltip />} />

              {showEarned && (
                <Area
                  type="monotone"
                  dataKey="earned"
                  name="Earned"
                  stroke="#3b82f6"
                  strokeWidth={2.8}
                  fill="url(#earnedAreaGradient)"
                  activeDot={{
                    r: 6,
                    stroke: '#ffffff',
                    strokeWidth: 3,
                    fill: '#2563eb',
                    className: 'filter drop-shadow-md',
                  }}
                  animationDuration={1300}
                  animationEasing="ease-out"
                />
              )}

              {showRedeemed && (
                <Area
                  type="monotone"
                  dataKey="redeemed"
                  name="Redeemed"
                  stroke="#10b981"
                  strokeWidth={2.8}
                  fill="url(#redeemedAreaGradient)"
                  activeDot={{
                    r: 6,
                    stroke: '#ffffff',
                    strokeWidth: 3,
                    fill: '#059669',
                    className: 'filter drop-shadow-md',
                  }}
                  animationDuration={1300}
                  animationEasing="ease-out"
                />
              )}
            </AreaChart>
          ) : (
            <BarChart
              data={pointsActivity}
              margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
              barGap={4}
            >
              <defs>
                <linearGradient id="earnedBarGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="100%" stopColor="#2563eb" />
                </linearGradient>
                <linearGradient id="redeemedBarGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="4 4"
                vertical={false}
                stroke="rgba(148, 163, 184, 0.25)"
              />

              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: 'rgba(100, 116, 139, 0.8)' }}
                tickFormatter={(val) => `D${val}`}
              />

              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: 'rgba(100, 116, 139, 0.8)' }}
                tickFormatter={(val) => `${val}k`}
              />

              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.05)', radius: 8 }} />

              {showEarned && (
                <Bar
                  dataKey="earned"
                  name="Earned"
                  fill="url(#earnedBarGradient)"
                  radius={[6, 6, 2, 2]}
                  animationDuration={1100}
                />
              )}

              {showRedeemed && (
                <Bar
                  dataKey="redeemed"
                  name="Redeemed"
                  fill="url(#redeemedBarGradient)"
                  radius={[6, 6, 2, 2]}
                  animationDuration={1100}
                />
              )}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
